import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, useFieldArray } from 'react-hook-form'
import { z } from 'zod'
import {
  useCreatePurchaseBill,
  useSuppliers,
  useProducts,
  usePurchaseOrders,
} from '@hooks/useProcurement'

const billItemSchema = z.object({
  productId: z.coerce.number().positive('Product is required'),
  qtyReceived: z.coerce.number().positive('Quantity must be greater than 0'),
  uom: z.string().min(1, 'UOM is required'),
  ratePerUnit: z.coerce.number().positive('Rate must be greater than 0'),
  expiryDate: z.string().optional().or(z.literal('')),
})

const billSchema = z.object({
  supplierId: z.coerce.number().positive('Supplier is required'),
  supplierBillNo: z.string().min(1, 'Supplier bill number is required'),
  billDate: z.string().refine((val) => !isNaN(Date.parse(val)), 'Invalid date'),
  purchaseOrderId: z.coerce.number().optional(),
  items: z.array(billItemSchema).min(1, 'At least one item is required'),
})

type BillFormData = z.infer<typeof billSchema>

export function PurchaseBillEntryPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const { data: suppliersData } = useSuppliers()
  const { data: productsData } = useProducts()
  const { data: posData } = usePurchaseOrders('')
  const create = useCreatePurchaseBill()

  const suppliers = suppliersData?.data?.items || []
  const products = productsData?.data?.items || []
  const purchaseOrders = posData?.data?.items || []

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<BillFormData>({
    defaultValues: {
      supplierId: 0,
      supplierBillNo: '',
      billDate: new Date().toISOString().split('T')[0],
      purchaseOrderId: undefined,
      items: [{ productId: 0, qtyReceived: 0, uom: 'KG', ratePerUnit: 0, expiryDate: '' }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  })

  const selectedItems = watch('items')
  const selectedSupplierId = watch('supplierId')
  const selectedPoId = watch('purchaseOrderId')

  const filteredPos = selectedSupplierId
    ? purchaseOrders.filter((po) => po.supplierId === selectedSupplierId)
    : []

  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => {
      const qty = parseFloat(String(item.qtyReceived)) || 0
      const rate = parseFloat(String(item.ratePerUnit)) || 0
      return sum + qty * rate
    }, 0)
  }

  const onSubmit = async (data: BillFormData) => {
    try {
      setError(null)
      setSuccessMessage(null)

      const formattedData = {
        ...data,
        billDate: new Date(data.billDate).toISOString(),
        purchaseOrderId: data.purchaseOrderId ? data.purchaseOrderId : undefined,
        items: data.items.map((item) => ({
          ...item,
          expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString() : undefined,
        })),
      }

      await create.mutateAsync(formattedData)
      setSuccessMessage('Bill submitted successfully! Stock batches created.')
      setTimeout(() => {
        navigate('/dashboard/procurement/purchase-bills')
      }, 2000)
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          'Failed to submit bill. Please check the data and try again.'
      )
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Receive & Record Purchase Bill
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Create stock batches (REPACK and DIRECT_SALE) from supplier bills
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/procurement/purchase-bills')}
          className="btn-secondary"
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success">
            {successMessage}
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Bill Header</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier *
              </label>
              <select {...register('supplierId')} className="w-full">
                <option value="">Select Supplier</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
              {errors.supplierId && (
                <span className="text-red-500 text-sm">
                  {errors.supplierId.message}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Bill No. *
              </label>
              <input
                {...register('supplierBillNo')}
                type="text"
                placeholder="e.g., INV-2026-0891"
                className="w-full"
              />
              {errors.supplierBillNo && (
                <span className="text-red-500 text-sm">
                  {errors.supplierBillNo.message}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bill Date *
              </label>
              <input
                {...register('billDate')}
                type="date"
                className="w-full"
              />
              {errors.billDate && (
                <span className="text-red-500 text-sm">
                  {errors.billDate.message}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link to Purchase Order (Optional)
              </label>
              <select {...register('purchaseOrderId')} className="w-full">
                <option value="">No Purchase Order</option>
                {filteredPos.map((po) => (
                  <option key={po.id} value={po.id}>
                    {po.poNumber} ({po.supplier.name})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              Received Items (Auto-creates REPACK/DIRECT_SALE Batches)
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              Select products and enter received quantities. REPACK items create BulkStockBatch.
              DIRECT_SALE items create FinishedGoodsBatch.
            </p>
          </div>

          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 font-semibold">Product</th>
                  <th className="text-left px-4 py-2 font-semibold">Type</th>
                  <th className="text-left px-4 py-2 font-semibold">UOM</th>
                  <th className="text-right px-4 py-2 font-semibold">Qty Received</th>
                  <th className="text-right px-4 py-2 font-semibold">Rate/Unit</th>
                  <th className="text-left px-4 py-2 font-semibold">Expiry (Optional)</th>
                  <th className="text-right px-4 py-2 font-semibold">Amount</th>
                  <th className="text-center px-4 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => {
                  const product = products.find(
                    (p) => p.id === selectedItems[index]?.productId
                  )
                  const qty = parseFloat(String(selectedItems[index]?.qtyReceived)) || 0
                  const rate = parseFloat(String(selectedItems[index]?.ratePerUnit)) || 0
                  const amount = qty * rate

                  return (
                    <tr key={field.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <select
                          {...register(`items.${index}.productId`, {
                            valueAsNumber: true,
                          })}
                          className="w-full text-sm"
                        >
                          <option value="">Select Product</option>
                          {products.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name}
                            </option>
                          ))}
                        </select>
                        {errors.items?.[index]?.productId && (
                          <span className="text-red-500 text-xs">
                            {errors.items[index].productId?.message}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`badge badge-sm ${
                            product?.productType === 'REPACK'
                              ? 'badge-success'
                              : 'badge-warning'
                          }`}
                        >
                          {product?.productType === 'REPACK' ? 'Repack' : 'Direct'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          {...register(`items.${index}.uom`)}
                          type="text"
                          className="w-24 text-sm"
                          disabled
                          value={product?.baseUom || 'KG'}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          {...register(`items.${index}.qtyReceived`, {
                            valueAsNumber: true,
                          })}
                          type="number"
                          min="0"
                          step="0.1"
                          className="w-24 text-right text-sm"
                        />
                        {errors.items?.[index]?.qtyReceived && (
                          <span className="text-red-500 text-xs">
                            {errors.items[index].qtyReceived?.message}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <input
                          {...register(`items.${index}.ratePerUnit`, {
                            valueAsNumber: true,
                          })}
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-28 text-right text-sm"
                        />
                        {errors.items?.[index]?.ratePerUnit && (
                          <span className="text-red-500 text-xs">
                            {errors.items[index].ratePerUnit?.message}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <input
                          {...register(`items.${index}.expiryDate`)}
                          type="date"
                          className="w-32 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900">
                        ₹{amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-300">
                  <td colSpan={6} className="px-4 py-3 text-right font-semibold">
                    Total Amount:
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-lg">
                    ₹{calculateTotal().toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <button
            type="button"
            onClick={() =>
              append({
                productId: 0,
                qtyReceived: 0,
                uom: 'KG',
                ratePerUnit: 0,
                expiryDate: '',
              })
            }
            className="btn-secondary text-sm"
          >
            + Add Item
          </button>

          {errors.items && (
            <span className="text-red-500 text-sm block mt-2">
              {errors.items.message}
            </span>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard/procurement/purchase-bills')}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary disabled:opacity-50"
            disabled={isSubmitting || create.isPending}
          >
            {isSubmitting || create.isPending ? 'Submitting...' : 'Submit Bill & Create Batches'}
          </button>
        </div>
      </form>
    </div>
  )
}
